/**
 * Removes brackets and their content from student names
 * @param {string} name - The original name with potential brackets
 * @returns {string} - The cleaned name without brackets
 */
export const removeNameBrackets = (name) => {
	if (!name || typeof name !== "string") {
		return "";
	}

	// Remove all content within brackets (including the brackets themselves)
	// This regex handles: (), [], {}, 〈〉, 【】, etc.
	const cleanedName = name
		.replace(/\([^)]*\)/g, "") // Remove content in parentheses
		.replace(/\[[^\]]*\]/g, "") // Remove content in square brackets
		.replace(/\{[^}]*\}/g, "") // Remove content in curly brackets
		.replace(/〈[^〉]*〉/g, "") // Remove content in angle brackets (Asian)
		.replace(/【[^】]*】/g, "") // Remove content in corner brackets (Asian)
		.replace(/\s+/g, " ") // Replace multiple spaces with single space
		.trim(); // Remove leading/trailing whitespace

	return cleanedName;
};

/**
 * Formats a full student name by removing brackets and ensuring proper capitalization
 * @param {string} firstName - Student's first name
 * @param {string} lastName - Student's last name
 * @returns {string} - Cleaned and formatted full name
 */
export const formatStudentName = (firstName, lastName) => {
	const cleanFirst = removeNameBrackets(firstName);
	const cleanLast = removeNameBrackets(lastName);

	return `${cleanFirst} ${cleanLast}`.trim();
};

/**
 * Gets display name from a student object
 * @param {Object} student - Student object with firstName and lastName
 * @returns {string} - Cleaned display name
 */
export const getStudentDisplayName = (student) => {
	if (!student) return "";

	const { firstName = "", lastName = "" } = student;
	return formatStudentName(firstName, lastName);
};

/**
 * Capitalizes the first letter of each word (useful for names)
 * @param {string} name - Name to capitalize
 * @returns {string} - Properly capitalized name
 */
export const capitalizeNameWords = (name) => {
	if (!name || typeof name !== "string") {
		return "";
	}

	return name
		.toLowerCase()
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
};

/**
 * Advanced name formatter that removes brackets and applies proper capitalization
 * @param {string} firstName - Student's first name
 * @param {string} lastName - Student's last name
 * @param {boolean} autoCapitalize - Whether to auto-capitalize names (default: false)
 * @returns {string} - Fully processed display name
 */
export const processStudentName = (
	firstName,
	lastName,
	autoCapitalize = false
) => {
	let cleanName = formatStudentName(firstName, lastName);

	if (autoCapitalize) {
		cleanName = capitalizeNameWords(cleanName);
	}

	return cleanName;
};

// Export default for convenience
// eslint-disable-next-line import/no-anonymous-default-export
export default {
	removeNameBrackets,
	formatStudentName,
	getStudentDisplayName,
	capitalizeNameWords,
	processStudentName,
};
